import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  fetchEmailPreferences,
  updateEmailPreferences,
} from '../../api/account';

const defaultPreferences = {
  eventInvitations: true,
  groupActivity: true,
  productUpdates: true,
  marketing: false,
};

const preferenceKeys = [
  'eventInvitations',
  'groupActivity',
  'productUpdates',
  'marketing',
];

const normalizePreferences = (preferences = {}) => ({
  ...defaultPreferences,
  eventInvitations: preferences.eventInvitations !== false,
  groupActivity: preferences.groupActivity !== false,
  productUpdates: preferences.productUpdates !== false,
  marketing: preferences.marketing === true,
});

const EmailPreferencesPanel = () => {
  const { t } = useTranslation();
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;

    fetchEmailPreferences()
      .then((data) => {
        if (!mounted) return;
        setPreferences(normalizePreferences(data.preferences));
        setError(false);
      })
      .catch(() => {
        if (mounted) setError(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleToggle = (key) => (event) => {
    setSuccess(false);
    setError(false);
    setPreferences((current) => ({
      ...current,
      [key]: event.target.checked,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccess(false);
    setError(false);

    try {
      const data = await updateEmailPreferences(preferences);

      setPreferences(normalizePreferences(data.preferences));
      setSuccess(true);
    } catch {
      setError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="subtitle2" color="secondary.dark" sx={{ mb: 0.5 }}>
          {t('notifications.eyebrow')}
        </Typography>
        <Typography variant="h3">{t('notifications.email.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {t('notifications.email.subtitle')}
        </Typography>
      </Box>

      {loading ? (
        <Stack direction="row" spacing={1.5} alignItems="center">
          <CircularProgress size={20} />
          <Typography color="text.secondary">
            {t('notifications.email.loading')}
          </Typography>
        </Stack>
      ) : (
        <Stack spacing={1}>
          {preferenceKeys.map((key) => (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  checked={Boolean(preferences[key])}
                  onChange={handleToggle(key)}
                />
              }
              label={
                <Box>
                  <Typography variant="subtitle2">
                    {t(`notifications.email.options.${key}.label`)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(`notifications.email.options.${key}.description`)}
                  </Typography>
                </Box>
              }
              sx={{
                alignItems: 'flex-start',
                ml: 0,
                gap: 1,
                '& .MuiFormControlLabel-label': { flex: 1 },
              }}
            />
          ))}
        </Stack>
      )}

      {success ? (
        <Alert severity="success">{t('notifications.email.success')}</Alert>
      ) : null}
      {error ? (
        <Alert severity="error">{t('notifications.email.error')}</Alert>
      ) : null}

      <Button
        variant="contained"
        onClick={handleSave}
        disabled={loading || saving}
        sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
      >
        {saving ? t('notifications.email.saving') : t('notifications.email.save')}
      </Button>
    </Stack>
  );
};

export default EmailPreferencesPanel;
